import { Button, Typography } from '@mui/material';
import React from 'react';
import type { Holder } from '../../data/types';
import type { CheckState, PersonReadiness } from '../../domain/kyc/readiness';
import { peopleFor } from '../../domain/kyc/readiness';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { useToast } from '../../ui/feedback';
import { SectionCard, StatusChip } from '../../ui/primitives';

const CHECK: Record<
  CheckState,
  { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }
> = {
  OK: { label: 'Verified', tone: 'success' },
  PENDING: { label: 'In progress', tone: 'warning' },
  ATTENTION: { label: 'Needed', tone: 'danger' },
  NOT_NEEDED: { label: 'Not needed', tone: 'neutral' },
};

function Check({ state }: { state: CheckState }) {
  return <StatusChip status={state} label={CHECK[state].label} tone={CHECK[state].tone} />;
}

export default function PeopleChecks({ holder }: { holder: Holder }) {
  const { dispatch } = useRegistry();
  const scope = useTenantScope();
  const toast = useToast();
  return (
    <SectionCard
      title="People"
      subtitle="Everyone who controls this investor, checked individually"
      noPadding
    >
      <DataTable<PersonReadiness>
        rows={peopleFor(holder, scope.kycCases)}
        rowKey={(row) => row.id}
        dense
        columns={[
          {
            key: 'name',
            label: 'Person',
            render: (row) => (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.role}
                </Typography>
              </>
            ),
          },
          { key: 'identity', label: 'Identity', render: (row) => <Check state={row.identity} /> },
          {
            key: 'pep',
            label: 'PEP',
            render: (row) =>
              row.pep ? (
                <StatusChip status="PEP" label="PEP" tone="warning" />
              ) : (
                <StatusChip status="CLEAR" label="Clear" tone="success" />
              ),
          },
          {
            key: 'wealth',
            label: 'Source of wealth',
            render: (row) =>
              row.sourceOfWealth === 'ATTENTION' ? (
                <Button
                  size="small"
                  onClick={() => {
                    dispatch({ type: 'HOLDER_SOW_VERIFY', holderId: holder.id, personId: row.id });
                    toast('Source of wealth recorded as verified');
                  }}
                >
                  Mark verified
                </Button>
              ) : (
                <Check state={row.sourceOfWealth} />
              ),
          },
        ]}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        component="div"
        sx={{ px: 2.5, py: 1.5 }}
      >
        Source of wealth is only asked for when someone is a PEP or the investor is rated high risk,
        so most investors are never asked for it.
      </Typography>
    </SectionCard>
  );
}
